import axios from "axios";
import { GLOBAL_API } from "../actionTypes/types";
import { globalAlert } from "../actions/commonActions";
import {
  LEARNER_ROUTE_PREFIX,
  resolveLmsShortName,
  learnerPath,
} from "../lms/_learnerConfig";
import {
  getRequestLanguage,
  shouldSendRequestLanguage,
} from "../i18n/requestLanguage";

// Single shared API client for the LMS surface (admin + learner portal).
// Responsibilities the rest of the app's axiosUtility does NOT cover:
//  • inject ecauthtoken + ecsecuretoken on every request
//  • capture REFRESHED tokens off each response's headers (sliding renewal) and
//    persist them, so long sessions never expire mid-use
//  • 401 → clear tokens and bounce to login
//  • 429 → surface a friendly "rate limited" message
// Callers keep using the { status, data, message } envelope; app-status 204 is a
// soft error they already handle (show message), so we pass it through.

const lms = axios.create({ baseURL: GLOBAL_API, withCredentials: true });

const isLearnerSurface = () => {
  const path = window.location.pathname.toLowerCase();
  return (
    path.startsWith(`/${LEARNER_ROUTE_PREFIX}/`) ||
    path === "/setpassword" ||
    path === "/set-password"
  );
};

const authHeaders = () => {
  const h = {};
  const a = localStorage.getItem("ecauthtoken");
  if (a) h.ecauthtoken = a;
  // Admin authentication still uses the legacy header contract. Learner
  // refresh tokens are HttpOnly cookies and must never be read by this bundle.
  if (!isLearnerSurface()) {
    const s = localStorage.getItem("ecsecuretoken");
    if (s) h.ecsecuretoken = s;
  }
  if (shouldSendRequestLanguage()) {
    const lang = getRequestLanguage();
    h["Accept-Language"] = lang;
    h["X-App-Language"] = lang;
  }
  return h;
};

// Capture sliding-renewal tokens returned on any authenticated response.
const captureRenewedTokens = (headers = {}) => {
  const a = headers.ecauthtoken || headers.Ecauthtoken;
  if (a) localStorage.setItem("ecauthtoken", a);
  if (!isLearnerSurface()) {
    const s = headers.ecsecuretoken || headers.Ecsecuretoken;
    if (s) localStorage.setItem("ecsecuretoken", s);
  }
};

lms.interceptors.request.use((config) => {
  config.headers = { ...(config.headers || {}), ...authHeaders() };
  if (!shouldSendRequestLanguage()) {
    delete config.headers["Accept-Language"];
    delete config.headers["X-App-Language"];
  }
  return config;
});

lms.interceptors.response.use(
  (response) => {
    captureRenewedTokens(response.headers);
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      localStorage.removeItem("ecauthtoken");
      localStorage.removeItem("ecsecuretoken");
      const loginPath = isLearnerSurface()
        ? learnerPath(resolveLmsShortName(), "login")
        : "/admin/login";
      if (window.location.pathname !== loginPath) {
        window.location.href = loginPath;
      }
    } else if (status === 429) {
      globalAlert(
        "warning",
        error?.response?.data?.message || "Too many requests — please slow down."
      );
    }
    return Promise.reject(error);
  }
);

export const lmsGet = (url) => lms.get(url);
export const lmsPost = (url, data, config) => lms.post(url, data, config);
export const lmsDelete = (url) => lms.delete(url);

export default lms;
